import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useInvoiceSettings } from '@/hooks/useInvoiceSettings';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import { translateSupabaseError } from '@/utils/errorTranslation';
import { readEdgeFunctionError } from '@/lib/edgeFunctionError';
import type {
  PendingPurchaseInvoice,
  DfeSyncLog,
  ManifestationType,
  PendingInvoiceStatus
} from '@/types/purchaseInvoice';

/**
 * Espelho de supabase/functions/_shared/fiscal-provider.ts:59-61 (tpAmbFromSettings):
 * 'producao' → 1; qualquer outro valor → 2 (fail-safe homologação). A pending, o cursor
 * e o bucket dfe-documents são por ambiente (UNIQUE ternária) — a lista só mostra o vigente.
 */
export const tpAmbFromSettings = (value: string | null | undefined): 1 | 2 =>
  value === 'producao' ? 1 : 2;

const DFE_BUCKET = 'dfe-documents';

/** R-NF.13: textos de toast das recusas (210220 / 210240). Demais tipos usam o texto genérico. */
const RECUSA_TOAST: Partial<Record<ManifestationType, { sucesso: string; erro: string }>> = {
  desconhecimento: {
    sucesso: 'Desconhecimento da operação registrado na SEFAZ',
    erro: 'SEFAZ não registrou o desconhecimento da operação',
  },
  nao_realizada: {
    sucesso: 'Operação não realizada registrada na SEFAZ',
    erro: 'SEFAZ não registrou a operação não realizada',
  },
};

/** R-NF.7 (D-FNF.2): busca server-side ativa a partir deste tamanho; suspende o filtro de mês. */
const BUSCA_MIN_CHARS = 4;

export function usePurchaseInvoices(month?: Date, searchTerm?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { settings: invoiceSettings, isLoading: settingsLoading } = useInvoiceSettings();
  const ambiente = tpAmbFromSettings(invoiceSettings?.nfe_environment);

  // R-NF.7: termo normalizado — vírgula/parêntese quebram a sintaxe do .or() do PostgREST
  const termo = (searchTerm ?? '').trim().replace(/[,()]/g, ' ').replace(/\s+/g, ' ').trim();
  const buscaAtiva = termo.length >= BUSCA_MIN_CHARS;

  // Get user's company ID
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const companyId = profile?.company_id;

  // Fetch pending invoices — só o ambiente vigente do tenant (produção × homologação nunca se misturam)
  const pendingInvoicesQuery = useQuery({
    queryKey: ['pending-purchase-invoices', companyId, ambiente, month?.toISOString(), buscaAtiva ? termo : ''],
    queryFn: async () => {
      if (!companyId) return [];

      let query = supabase
        .from('purchase_invoices_pending')
        .select('*')
        .eq('company_id', companyId)
        .eq('ambiente', ambiente)
        .order('emission_date', { ascending: false });

      // R-NF.7: busca server-side (chave, número, fornecedor, CNPJ) — cruza meses
      if (buscaAtiva) {
        const pattern = `%${termo}%`;
        const digitos = termo.replace(/\D/g, '');
        const filtros = [
          `invoice_number.ilike.${pattern}`,
          `emitente_razao_social.ilike.${pattern}`,
          ...(digitos ? [`access_key.ilike.%${digitos}%`, `emitente_cnpj.ilike.%${digitos}%`] : []),
        ];
        query = query.or(filtros.join(','));
      }

      // Filter by month if provided (suspenso quando a busca está ativa)
      if (month && !buscaAtiva) {
        const startOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
        const endOfMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0, 23, 59, 59);
        
        query = query
          .gte('emission_date', startOfMonth.toISOString())
          .lte('emission_date', endOfMonth.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      
      return data as PendingPurchaseInvoice[];
    },
    enabled: !!companyId && !settingsLoading,
  });

  // Fetch last sync log
  const lastSyncQuery = useQuery({
    queryKey: ['dfe-last-sync', companyId],
    queryFn: async () => {
      if (!companyId) return null;

      const { data, error } = await supabase
        .from('dfe_sync_log')
        .select('*')
        .eq('company_id', companyId)
        .order('last_sync_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as DfeSyncLog | null;
    },
    enabled: !!companyId,
  });

  // Sync invoices mutation — Arco A: Edge dfe-sync (motor próprio); force_resync morto (D3)
  const syncInvoicesMutation = useMutation({
    mutationFn: async () => {
      logger.log('[PurchaseInvoices] Iniciando sincronização de notas...', {
        companyId,
        timestamp: new Date().toISOString(),
      });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Não autenticado');

      const response = await supabase.functions.invoke('dfe-sync', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: {},
      });

      if (response.error) {
        throw new Error(response.error.message || 'Erro ao sincronizar notas');
      }
      // A Edge responde 200 com success:false em erro de motor/gate — não deixar virar "nenhum documento"
      if (response.data?.success === false) {
        throw new Error(response.data.error || response.data.message || 'Erro ao sincronizar notas');
      }

      return response.data;
    },
    onSuccess: (data) => {
      logger.log('[PurchaseInvoices] Sincronização concluída:', {
        documents_found: data.documents_found,
        documents_new: data.documents_new,
        rate_limit: data.rate_limit,
        not_configured: data.not_configured,
        needs_more_sync: data.needs_more_sync,
        timestamp: new Date().toISOString(),
      });
      
      queryClient.invalidateQueries({ queryKey: ['pending-purchase-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['dfe-last-sync'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-invoice-counts'] });

      // Handle rate limit - show warning toast instead of error
      if (data.rate_limit) {
        toast.warning(data.message || 'SEFAZ temporariamente indisponível. Aguarde 1 hora para nova consulta.', {
          duration: 8000,
        });
        return;
      }

      // Handle not configured - show warning toast
      if (data.not_configured) {
        toast.warning(data.message || 'Distribuição NF-e não configurada para este CNPJ.', {
          duration: 8000,
        });
        return;
      }

      // Handle needs more sync - show info toast with action button
      if (data.needs_more_sync) {
        toast.info(data.message, {
          duration: 10000,
          action: {
            label: 'Continuar',
            onClick: () => syncInvoicesMutation.mutate(),
          },
        });
        return;
      }
      
      if (data.documents_new > 0) {
        toast.success(`${data.documents_new} nova(s) nota(s) encontrada(s)!`);
      } else if (data.documents_found > 0) {
        toast.info('Nenhuma nota nova encontrada');
      } else {
        toast.info('Nenhum documento encontrado');
      }
    },
    onError: (error: Error) => {
      logger.error('[PurchaseInvoices] Erro na sincronização:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      toast.error(`Erro ao sincronizar: ${error.message}`);
    },
  });

  // Manifest invoice mutation
  const manifestInvoiceMutation = useMutation({
    mutationFn: async ({ 
      invoiceId, 
      manifestationType, 
      justification 
    }: { 
      invoiceId: string; 
      manifestationType: ManifestationType; 
      justification?: string;
    }) => {
      logger.log('[PurchaseInvoices] Manifestando nota:', {
        invoiceId,
        manifestationType,
        justification: justification ? 'provided' : 'none',
        timestamp: new Date().toISOString(),
      });
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Não autenticado');

      const response = await supabase.functions.invoke('manifest-purchase-invoice', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: {
          invoice_id: invoiceId,
          manifestation_type: manifestationType,
          justification,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Erro ao manifestar nota');
      }
      // Arco A / C2: a Edge responde 200 com success:false (cStat rejeitado, motor, ambiente, UPDATE)
      if (response.data?.success === false) {
        throw new Error(response.data.error || 'Erro ao manifestar nota');
      }

      return response.data;
    },
    onSuccess: (_, variables) => {
      logger.log('[PurchaseInvoices] Manifestação concluída:', {
        invoiceId: variables.invoiceId,
        manifestationType: variables.manifestationType,
        timestamp: new Date().toISOString(),
      });
      queryClient.invalidateQueries({ queryKey: ['pending-purchase-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-invoice-counts'] });
      // R-NF.13: toast por tipo só para as recusas; ciência/confirmação mantêm o texto atual
      toast.success(
        RECUSA_TOAST[variables.manifestationType]?.sucesso ?? 'Manifestação registrada com sucesso',
      );
    },
    onError: (error: Error, variables) => {
      logger.error('[PurchaseInvoices] Erro na manifestação:', {
        invoiceId: variables.invoiceId,
        manifestationType: variables.manifestationType,
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      // R-NF.5: a Edge devolve `${cStat}: ${xMotivo}` cru; 596 = evento fora do prazo
      if (error.message.startsWith('596')) {
        toast.error('SEFAZ recusou: evento fora do prazo (596)', {
          description: error.message,
          duration: 8000,
        });
        return;
      }
      // R-NF.13: recusa rejeitada — o motivo cru da SEFAZ (`${cStat}: ${xMotivo}`) vem da Edge
      const recusa = RECUSA_TOAST[variables.manifestationType];
      if (recusa) {
        toast.error(recusa.erro, { description: error.message, duration: 8000 });
        return;
      }
      toast.error(`Erro na manifestação: ${error.message}`);
    },
  });

  // Update invoice status
  const updateInvoiceStatusMutation = useMutation({
    mutationFn: async ({ 
      invoiceId, 
      status,
      linkedPurchaseId,
    }: { 
      invoiceId: string; 
      status: PendingInvoiceStatus;
      linkedPurchaseId?: string;
    }) => {
      const updateData: any = {
        status,
        updated_at: new Date().toISOString(),
      };

      if (linkedPurchaseId) {
        updateData.linked_purchase_id = linkedPurchaseId;
      }

      if (status === 'processed' || status === 'linked') {
        updateData.processed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('purchase_invoices_pending')
        .update(updateData)
        .eq('id', invoiceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-purchase-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-invoice-counts'] });
    },
  });

  // Ignore invoice mutation
  const ignoreInvoiceMutation = useMutation({
    mutationFn: async (invoiceId: string) => {
      const { error } = await supabase
        .from('purchase_invoices_pending')
        .update({ 
          status: 'ignored',
          updated_at: new Date().toISOString(),
        })
        .eq('id', invoiceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-purchase-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-invoice-counts'] });
      toast.success('Nota ignorada');
    },
  });

  /**
   * Lê o XML completo (procNFe) do bucket privado dfe-documents. A policy
   * dfe_documents_select já gateia tenant (foldername[1] = company_id) e feature
   * (purchasing:manage) — sem Edge intermediária. Devolve a string que
   * parse-nfe-xml já espera em body.xmlContent.
   */
  const downloadDfeXml = async (xmlStoragePath: string): Promise<string> => {
    const { data, error } = await supabase.storage.from(DFE_BUCKET).download(xmlStoragePath);
    if (error || !data) {
      logger.error('[PurchaseInvoices] Falha ao ler XML do bucket:', { xmlStoragePath, error });
      throw new Error(error ? translateSupabaseError(error) : 'XML não encontrado no armazenamento');
    }
    return data.text();
  };

  /**
   * R-NF.12: DANFE da nota de fornecedor. A Edge render-danfe lê o procNFe do bucket e pede
   * o render ao motor fiscal (sem o logo do tenant — E-NF12.1). Devolve o PDF pronto p/ download.
   */
  const downloadDanfePdf = async (pendingId: string): Promise<{ blob: Blob; filename: string }> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Não autenticado');

    const { data, error } = await supabase.functions.invoke('render-danfe', {
      headers: { Authorization: `Bearer ${session.access_token}` },
      body: { pending_id: pendingId },
    });

    if (error) {
      const motivo = await readEdgeFunctionError(error);
      logger.error('[PurchaseInvoices] Falha ao gerar DANFE:', { pendingId, motivo });
      throw new Error(motivo);
    }
    if (!data?.success || !data?.pdf_base64) {
      throw new Error(data?.error || 'Falha ao gerar o DANFE');
    }

    const byteCharacters = atob(data.pdf_base64);
    const bytes = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) bytes[i] = byteCharacters.charCodeAt(i);
    return {
      blob: new Blob([bytes], { type: 'application/pdf' }),
      filename: data.filename || `danfe_${pendingId}.pdf`,
    };
  };

  // Check fiscal status mutation
  const checkFiscalStatusMutation = useMutation({
    mutationFn: async () => {
      logger.log('[PurchaseInvoices] Executando diagnóstico fiscal...', {
        companyId,
        timestamp: new Date().toISOString(),
      });
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Não autenticado');

      const response = await supabase.functions.invoke('check-fiscal-status', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Erro ao verificar status fiscal');
      }

      return response.data;
    },
    onSuccess: (data) => {
      logger.log('[PurchaseInvoices] Diagnóstico concluído:', data);
    },
    onError: (error: Error) => {
      logger.error('[PurchaseInvoices] Erro no diagnóstico:', error.message);
      toast.error(`Erro no diagnóstico: ${error.message}`);
    },
  });

  return {
    // Data
    pendingInvoices: pendingInvoicesQuery.data || [],
    lastSync: lastSyncQuery.data,
    ambiente,
    /** R-NF.7: true quando o termo (≥ BUSCA_MIN_CHARS) está filtrando no banco e o mês foi suspenso */
    buscaAtiva,
    isLoading: pendingInvoicesQuery.isLoading || settingsLoading,
    isSyncing: syncInvoicesMutation.isPending,
    isCheckingFiscalStatus: checkFiscalStatusMutation.isPending,

    // Actions — manifest/updateStatus como mutateAsync: os chamadores fazem `await` e
    // precisam de conclusão + propagação de erro reais (antes: .mutate = void)
    syncInvoices: () => syncInvoicesMutation.mutate(),
    manifestInvoice: manifestInvoiceMutation.mutateAsync,
    updateInvoiceStatus: updateInvoiceStatusMutation.mutateAsync,
    ignoreInvoice: ignoreInvoiceMutation.mutate,
    downloadDfeXml,
    downloadDanfePdf,
    checkFiscalStatus: checkFiscalStatusMutation.mutateAsync,
    
    // Refetch
    refetch: () => {
      pendingInvoicesQuery.refetch();
      lastSyncQuery.refetch();
    },
  };
}

// Hook to get invoice counts by status
export function usePurchaseInvoiceCounts() {
  const { user } = useAuth();

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
  });

  const companyId = profile?.company_id;

  return useQuery({
    queryKey: ['purchase-invoice-counts', companyId],
    queryFn: async () => {
      if (!companyId) return { pending: 0, processed: 0, linked: 0, ignored: 0 };

      const { data, error } = await supabase
        .from('purchase_invoices_pending')
        .select('status')
        .eq('company_id', companyId);

      if (error) throw error;

      const counts = {
        pending: 0,
        processed: 0,
        linked: 0,
        ignored: 0,
      };

      for (const invoice of data || []) {
        const status = invoice.status as keyof typeof counts;
        if (counts[status] !== undefined) {
          counts[status]++;
        }
      }

      return counts;
    },
    enabled: !!companyId,
  });
}
